/**
 * ENCUADRE DEL MAPA
 * ============================================================================
 * POR QUÉ EXISTE ESTE ARCHIVO
 *
 * El fundador probó la app con dos teléfonos y reportó: "no se puede apreciar
 * la línea que se marca al pedir el viaje... para poder verla hay que mover el
 * mapa a mano y centralizarla".
 *
 * La causa no era la línea: era la CÁMARA. En todo el proyecto no había una
 * sola llamada a `fitToCoordinates`. Los dos únicos movimientos de cámara
 * centraban en UN punto con un zoom fijo (~1,3 km de alto visible), y un centro
 * con zoom fijo no puede contener dos puntos: si el chofer estaba a más de unos
 * 600 metros del pasajero, el otro extremo de la línea caía fuera de la
 * pantalla. Se dibujaba bien, pero nadie la veía.
 *
 * Esta función es la pieza que faltaba, y está aparte del componente a
 * propósito: si algo aquí falla, el mapa se queda comportándose como hoy en
 * lugar de romperse.
 * ============================================================================
 */

export type Punto = { latitude: number; longitude: number } | null | undefined;

/** Grados de latitud a metros. Vale igual en cualquier parte del mundo. */
const METROS_POR_GRADO = 111_000;

/** Zoom por defecto cuando no hay nada que "encuadrar": ~1,11 km de alto. */
const DELTA_POR_DEFECTO = 0.01;

/**
 * Distancia aproximada en metros entre dos diferencias de grados.
 * La longitud se estrecha con la latitud (en RD, 18,5°, un grado de longitud
 * son 105 km y no 111): sin el coseno, el umbral de 120 m se comportaría como
 * 128 m en el eje este-oeste. Es poco, pero hacerlo bien es gratis.
 */
const gradosAMetros = (dLat: number, dLng: number, latitudDeReferencia: number) =>
  Math.hypot(dLat, dLng * Math.cos((latitudDeReferencia * Math.PI) / 180)) * METROS_POR_GRADO;

/** Lo que describe el último encuadre aplicado: dónde estaba y cuánto abarcaba. */
type Resumen = {
  /** Centro de la caja que engloba a todos los puntos. */
  lat: number;
  lng: number;
  /** Alto y ancho de esa caja, en grados. */
  spanLat: number;
  spanLng: number;
  /** Cuántos puntos había. */
  n: number;
};

export type EstadoEncuadre = {
  /** Hasta cuándo NO se re-encuadra porque el usuario está mirando a mano. */
  gestoHasta: number;
  /** Hasta cuándo NO se lanza otra animación, para no encolarlas. */
  animacionHasta: number;
  /** Último encuadre aplicado, para no repetirlo por moverse dos metros. */
  ultimo: Resumen | null;
};

export const nuevoEstadoEncuadre = (): EstadoEncuadre => ({
  gestoHasta: 0,
  animacionHasta: 0,
  ultimo: null,
});

/**
 * El usuario arrastró el mapa: durante unos segundos manda él.
 * Es la regla que más se nota cuando falta — un mapa que te devuelve al centro
 * cada vez que intentas mirar otra cosa es insoportable, y es justo lo que hace
 * que la gente sienta que "la app pelea con ella".
 */
export const marcarGestoDelUsuario = (e: EstadoEncuadre, ms = 12000) => {
  e.gestoHasta = Date.now() + ms;
};

/** El usuario pidió recentrar explícitamente: se le devuelve el control al mapa. */
export const limpiarGesto = (e: EstadoEncuadre) => {
  e.gestoHasta = 0;
};

export type OpcionesEncuadre = {
  puntos: Punto[];
  margenArribaDp?: number;
  margenAbajoDp?: number;
  margenLadosDp?: number;
  /** Petición explícita del usuario: se salta las guardas de gesto y animación. */
  forzar?: boolean;
  /** Metros mínimos de movimiento para volver a encuadrar. */
  minimoMetros?: number;
  /**
   * Zoom cuando NO hay nada que encuadrar de verdad: un punto suelto, o varios
   * puntos tan juntos que caben en un pañuelo. Es el alto visible en grados.
   */
  deltaUnPunto?: number;
};

export function encuadrarMapa(mapRef: any, e: EstadoEncuadre, o: OpcionesEncuadre): boolean {
  const m = mapRef?.current;

  // 1. GUARDA DE PLATAFORMA. En web el mapa es otra implementación (Leaflet) que
  //    no expone estos métodos: sin esta comprobación, la web reventaría.
  if (!m || typeof m.fitToCoordinates !== 'function') return false;

  // 2. LIMPIEZA. Fuera nulos, no-finitos, coordenadas fuera del planeta y el
  //    (0,0) del Golfo de Guinea, que es lo que sale cuando una coordenada
  //    llega vacía. Un solo (0,0) colado estira el encuadre hasta África y deja
  //    el viaje como un punto invisible; y una latitud de tres cifras (un
  //    número mal escalado, un parseo raro) es finita, colaba por el filtro
  //    viejo y le dejaba a `fitToCoordinates` una caja imposible.
  const ps = (o.puntos || []).filter(
    (p): p is { latitude: number; longitude: number } =>
      !!p &&
      Number.isFinite(p.latitude) &&
      Number.isFinite(p.longitude) &&
      p.latitude >= -90 &&
      p.latitude <= 90 &&
      p.longitude >= -180 &&
      p.longitude <= 180 &&
      !(p.latitude === 0 && p.longitude === 0)
  );
  if (ps.length === 0) return false;

  const ahora = Date.now();

  // 3. NO PELEAR CON EL DEDO, y 4. NO ENCOLAR ANIMACIONES que se pisen.
  if (!o.forzar && (ahora < e.gestoHasta || ahora < e.animacionHasta)) return false;

  // 5. LA CAJA QUE HAY QUE ENSEÑAR. Se calcula una vez y sirve para las tres
  //    decisiones que vienen: si hace falta re-encuadrar, si todo cabe en un
  //    pañuelo, y dónde centrar si así fuera.
  let minLat = ps[0].latitude;
  let maxLat = ps[0].latitude;
  let minLng = ps[0].longitude;
  let maxLng = ps[0].longitude;
  for (let i = 1; i < ps.length; i++) {
    const p = ps[i];
    if (p.latitude < minLat) minLat = p.latitude;
    if (p.latitude > maxLat) maxLat = p.latitude;
    if (p.longitude < minLng) minLng = p.longitude;
    if (p.longitude > maxLng) maxLng = p.longitude;
  }
  const spanLat = maxLat - minLat;
  const spanLng = maxLng - minLng;
  const centroLat = (minLat + maxLat) / 2;
  const centroLng = (minLng + maxLng) / 2;

  // 6. UMBRAL DE MOVIMIENTO. El GPS actualiza cada pocos segundos; re-encuadrar
  //    en cada latido marea al conductor y quema batería. Solo si de verdad ha
  //    cambiado algo.
  //
  //    Se mira la CAJA ENTERA, no el primer punto. Antes solo se comparaba
  //    `ps[0]`, y eso dejaba fuera el caso más obvio: el pasajero quieto que
  //    cambia el destino de la esquina a otra ciudad. Su posición —que es
  //    `ps[0]`— no se movía ni un metro, seguían siendo dos puntos, y la cámara
  //    se quedaba encuadrando el destino viejo. Ahora cuenta tanto que el
  //    conjunto se DESPLACE como que se ESTIRE o se ENCOJA.
  if (!o.forzar && e.ultimo && e.ultimo.n === ps.length) {
    const minimo = o.minimoMetros ?? 120;
    const seDesplazo = gradosAMetros(centroLat - e.ultimo.lat, centroLng - e.ultimo.lng, centroLat);
    const cambioDeTamano = gradosAMetros(
      spanLat - e.ultimo.spanLat,
      spanLng - e.ultimo.spanLng,
      centroLat
    );
    if (seDesplazo < minimo && cambioDeTamano < minimo) return false;
  }

  // 7. EL ZOOM DE "NO HAY NADA QUE ENCUADRAR". Un caller puede pasar 0, un
  //    negativo o un NaN sin querer, y los tres se traducen en zoom máximo, que
  //    es exactamente el fallo que este archivo existe para evitar.
  const d =
    typeof o.deltaUnPunto === 'number' && Number.isFinite(o.deltaUnPunto) && o.deltaUnPunto > 0
      ? o.deltaUnPunto
      : DELTA_POR_DEFECTO;

  const edgePadding = {
    top: o.margenArribaDp ?? 120,
    right: o.margenLadosDp ?? 48,
    bottom: o.margenAbajoDp ?? 40,
    left: o.margenLadosDp ?? 48,
  };

  // 8. ¿CABE TODO EN UN PAÑUELO?
  //
  //    Este es el caso de LOS DOS TELÉFONOS SOBRE LA MISMA MESA, que es como se
  //    prueba la app. Dos puntos separados por 5 metros son 0,000045° de caja:
  //    `fitToCoordinates` recibe un rectángulo degenerado, Google lo resuelve
  //    subiendo al zoom máximo (~21, unos 50 m de pantalla) y se ve un tejado.
  //    La línea está ahí y mide cinco metros, pero no hay nada reconocible
  //    alrededor y parece que la app está rota. Pasa en los dos teléfonos a la
  //    vez, porque a los dos les llega la misma caja.
  //
  //    El umbral es un tercio del zoom de un punto suelto: con el valor por
  //    defecto, 0,0033° ≈ 370 m. Va atado a `d` a propósito, para que haya un
  //    solo número que tocar. Por debajo de ese tamaño los pines caen dentro
  //    del tercio central de una vista de ~1,11 km —lejos de los bordes y de la
  //    tarjeta de arriba— y se ve la calle en lugar de la azotea. Por encima
  //    manda `fitToCoordinates`, que lo hace mejor porque respeta los márgenes.
  //
  //    Un punto solo es este mismo caso con la caja a cero, y los puntos todos
  //    idénticos también: ni uno ni otro necesitan rama aparte.
  const panuelo = d / 3;
  const cabeEnUnPanuelo = spanLat <= panuelo && spanLng <= panuelo;

  try {
    if (cabeEnUnPanuelo) {
      if (typeof m.animateToRegion === 'function') {
        m.animateToRegion(
          { latitude: centroLat, longitude: centroLng, latitudeDelta: d, longitudeDelta: d },
          600
        );
      } else {
        // Sin `animateToRegion` (implementaciones parciales del mapa) se pide lo
        // mismo con el método que sí existe: las esquinas de una caja de `d`
        // grados alrededor del centro. Antes esta rama era un crash, porque la
        // guarda de arriba solo comprobaba `fitToCoordinates`.
        const mitad = d / 2;
        m.fitToCoordinates(
          [
            { latitude: centroLat - mitad, longitude: centroLng - mitad },
            { latitude: centroLat + mitad, longitude: centroLng + mitad },
          ],
          { edgePadding, animated: true }
        );
      }
    } else {
      m.fitToCoordinates(ps, { edgePadding, animated: true });
    }
  } catch {
    // La promesa de este archivo es que un fallo aquí deje el mapa como está en
    // vez de tumbar la pantalla. Y sin marcar el encuadre como aplicado: si la
    // vista todavía no estaba montada, el siguiente latido lo vuelve a
    // intentar en lugar de quedarse convencido de que ya encuadró.
    return false;
  }

  e.animacionHasta = ahora + 700;
  e.ultimo = { lat: centroLat, lng: centroLng, spanLat, spanLng, n: ps.length };
  return true;
}
